<script setup>
import { useI18n } from 'vue-i18n'
import EventActivationButton from '@/components/EventActivationButton.vue'
import { formatDate, getMonthName } from '@/lib/dates'
import { eventTypeStyle } from '@/lib/eventTypes'
import { podiumColor } from '@/lib/podium'

/**
 * Light event cards of the play page: the event name with the icon of its type, my position badge
 * when I have one, the closing date and — for the stages — the activation switch. One card per row
 * of its column. No standings: the list behind them is the light one (see EventLib::list,
 * `?standings=0`), so the play page does not pay for the composition of the standings of every
 * running event. PlayEventList is the same card with the top of the standings.
 *
 * The name follows the rule of the event page: an event with a name of its own ("Championship of
 * September") shows that, built on the month it opens in (see EventDetailView); the others show
 * the label of their type.
 */
defineProps({
  /** Running events, in the shape returned by GET /api/events?standings=0. */
  events: { type: Array, default: () => [] },
})

const emit = defineEmits(['change'])

const { t, te } = useI18n()

/**
 * The activation choice of one card goes up with the event it belongs to (see PlayView).
 */
function onActivation(event, myActivation)
{
  emit('change', { id: event.id, myActivation })
}

function eventName(event)
{
  return te(`eventNames.${event.eventType}`)
    ? t(`eventNames.${event.eventType}`, { month: getMonthName(event.dateStart) })
    : t(`eventTypes.${event.eventType}`)
}
</script>

<template>
  <v-row>
    <v-col v-for="event in events" :key="event.id" cols="12" sm="6">
      <v-card
        :to="{ name: 'event_detail', params: { id: event.id } }"
        hover
        elevation="3"
        rounded="lg"
      >
        <v-card-item class="pa-4">
          <div class="d-flex align-center ga-2">
            <div class="flex-grow-1">
              <div class="d-flex align-center ga-2 mb-1">
                <p class="text-subtitle-1 font-weight-medium mb-0">{{ eventName(event) }}</p>
                <!-- A stage event this player activated: how many tracks are still to be raced.
                     Nothing when they are all done (see EventLib::present). -->
                <v-chip
                  v-if="event.myActivation && event.stagesLeft > 0"
                  size="x-small"
                  variant="tonal"
                  color="primary"
                  label
                >
                  {{ event.stagesLeft === 1
                    ? $t('events.stagesLeftOne')
                    : $t('events.stagesLeft', { n: event.stagesLeft }) }}
                </v-chip>
              </div>
              <p class="text-caption text-medium-emphasis mb-0">
                <v-icon size="14" class="mr-1">mdi-calendar-end</v-icon>
                {{ $t('events.deadline') }}: {{ formatDate(event.dateEnd) }}
              </p>
            </div>

            <v-chip
              v-if="event.myEntry?.position"
              :color="podiumColor(event.myEntry.position) ?? 'primary'"
              size="small"
              variant="tonal"
              label
            >
              {{ $t('events.position') }} {{ event.myEntry.position }}
            </v-chip>

            <v-icon
              :icon="eventTypeStyle(event.eventType).icon"
              :color="eventTypeStyle(event.eventType).color"
              size="28"
            />
          </div>
        </v-card-item>

        <!-- An event that asks for an explicit activation (a stage) carries its own switch at
             the bottom of the card — but only while it is off: turning it off is a choice for
             the event page (see EventActivationButton, hideWhenActive).
             The wrapper stops the click AND cancels its default action: the card is a link, so
             the stop alone would leave the browser free to follow it anyway. -->
        <template v-if="event.requiresActivation && event.playable && !event.myActivation">
          <v-divider />

          <v-card-actions class="px-4 py-3">
            <div class="w-100" @click.stop.prevent>
              <EventActivationButton :event="event" size="small" hide-when-active @change="onActivation(event, $event)" />
            </div>
          </v-card-actions>
        </template>
      </v-card>
    </v-col>
  </v-row>
</template>
